import { useAtomValue, useSetAtom } from "jotai";
import { hostDataAtom, modelDataAtom, themeDataAtom } from "../../atoms";
import _ from "lodash";
import apiAgent from "../../api/apiAgent";
import { tMsg } from "../ToastNotifications";
import { publishGroups } from "./publishConfig";
import { updatePublishTimes } from "./utils";
import { Dispatch, SetStateAction } from "react";

const getRelatedModels = (model: any, modelData: any) => {
  const relatedModels =
    model.relationships?.reduce(
      (relArr: string[], relationship: any): string[] => {
        if (!relationship.is_owner) return relArr;

        const modelName = relationship.related_to;
        const getDataForThisModel = modelData.find(
          (m: any) => m.name === modelName,
        );
        const addStrings = !getDataForThisModel
          ? []
          : getRelatedModels(getDataForThisModel, modelData);

        return [...relArr, modelName, ...addStrings];
      },
      [],
    ) || [];

  return relatedModels;
};

/**
 * Format field for Mildura hack
 * @param data
 * @param fieldNameToChange
 * @returns
 */
const formatFields = (
  data: any,
  fieldNameToChange: string[],
  themeData?: any[],
): any => {
  // Guard clause to ensure we are working with a valid object
  if (!data || typeof data !== "object" || Array.isArray(data)) {
    return data;
  }

  // Create a shallow copy to respect React immutability rules
  const updatedData = { ...data };

  fieldNameToChange.forEach((field) => {
    // 1. Check if the field actually exists in the object
    // 2. Ensure the value isn't already null or undefined
    // 3. Ensure we aren't accidentally wrapping an object that is already formatted
    if (
      Object.prototype.hasOwnProperty.call(updatedData, field) &&
      updatedData[field] !== null &&
      updatedData[field] !== undefined &&
      typeof updatedData[field] !== "object"
    ) {
      updatedData[field] = { url: updatedData[field] };
    }
  });

  if (_.has(data, "owner_stories_id")) {
    // return { ...updatedData, wall_story_id: data.owner_stories_id };
    updatedData.wall_story_id = data.owner_stories_id;
  }

  if (_.has(data, "themes") && themeData && themeData.length > 0) {
    updatedData.themes = updatedData.themes.reduce((arr: any[], item: any) => {
      const findTheme = themeData.find((i) => i.id === _.toNumber(item));
      if (findTheme) return [...arr, _.pick(findTheme, ["id", "name"])];
      else return arr;
    }, []);
    console.log("Themes", updatedData.themes);
  }

  return updatedData;
};

/**
 * Get and publish the models
 * @param models string of models to publish
 * @returns
 */
const usePublisher = (
  groupName: keyof typeof publishGroups,
  setPublishing: Dispatch<SetStateAction<boolean>>,
) => {
  // Step 1: get model data from locally stored data atom
  const modelData = useAtomValue(modelDataAtom);
  const hostData = useAtomValue(hostDataAtom);
  const themeData = useAtomValue(themeDataAtom);
  const { models, singleItems, atom } = publishGroups[groupName];
  const setPublishTimes = useSetAtom(atom);

  // to make filepath
  const makeFilePath = (objName: string) =>
    `${hostData.s3_endpoints_path}/${objName}.json`;

  // Step 1: validate models
  const validModels = models.reduce((arr: any[], model: string) => {
    const modelSchema = modelData.find((m) => m.name === model);
    if (!modelSchema) return arr;
    return [...arr, modelSchema];
  }, []);

  // Step 2: get model relationships to find out all getAll requests to be made
  // Use loop to find models of subrelations
  const callModels = _.uniq(
    validModels.reduce((arr: string[], model: any): string[] => {
      const relatedModels = getRelatedModels(model, modelData);
      return [...arr, model.name, ...relatedModels];
    }, []),
  );

  // Step 3: Put requests in an array
  const allModelRequests = callModels.map((model) =>
    apiAgent.DataType.getAll(model),
  );

  // Step 4: Make single items reequests
  const allSingleItemRequests = singleItems.map((si) =>
    apiAgent.SingleItem.get(si),
  );

  // Step 5a: Create function for getting children of a parent
  const getOneToManyItems = (
    ownerModel: string,
    ownerId: number,
    childItems: any[],
  ) => {
    const key = `owner_${ownerModel}_id`;
    const relatedItems = childItems.filter((ci) => ci[key] === ownerId);
    return relatedItems;
  };

  // Step 5b: Create function for looping through children to get subitems
  /**
   *
   * @param items
   * @param model
   * @param dataStore
   * @param parentPath model_name > item_id > model_name > item_id > etc..
   * @returns
   */
  const getAllRelationData = (
    items: any[],
    model: any,
    dataStore: any[],
    parentPath: string,
  ) => {
    const relationships = model.relationships || [];

    // Get Data relations
    const dataWithRelations = items.map((item: any) => {
      // Get new analytics path
      const pathWithId = `${parentPath}:${item.id}`;

      // Add path for analytics
      const relations = relationships.reduce((obj: any, rship: any) => {
        if (!rship.is_owner) return obj;

        // Get all items for the related model
        const modelItems =
          dataStore.find((ds) => ds.name === rship.related_to)?.data || [];

        // filter items that are related to parent ID
        const relatedItems =
          rship.type === "1-M"
            ? getOneToManyItems(model.name, item.id, modelItems)
            : item[`owner_${rship.related_to}_id`]?.map((id: number) =>
                modelItems.find((mi: any) => mi.id === id),
              );

        // Sort
        relatedItems.sort((a: any, b: any) => {
          const aval = a.sort_id + 1 || 9999999;
          const bval = b.sort_id + 1 || 9999999;
          return aval - bval;
        });

        // Get the model data of the child
        const getRelatedModel = modelData.find(
          (m) => m.name === rship.related_to,
        );

        // Get related children of child
        const getSubRelations = getRelatedModel
          ? getAllRelationData(
              relatedItems,
              getRelatedModel,
              dataStore,
              `${pathWithId}>${getRelatedModel.name}`,
            )
          : [];

        return {
          ...obj,
          [rship.related_to]: getSubRelations,
        };
      }, {});

      // Hack for mildura, change filename paths into
      // fieldName: { url: path }
      const formattedItem = formatFields(
        item,
        ["subtitles", "thumbnail", "file", "qr_image"],
        model.name === "stories" ? themeData : undefined,
      );

      // return { ...formattedItem, ...relations, analytics_path: pathWithId };
      return { ...formattedItem, ...relations, analytics_path: pathWithId };
    });

    dataWithRelations.sort((a: any, b: any) => {
      const aval = a.sort_id + 1 || 9999999;
      const bval = b.sort_id + 1 || 9999999;
      return aval - bval;
    });

    return dataWithRelations;
  };

  // Return requesting function
  return async () => {
    try {
      setPublishing(true);
      const results = await Promise.all(allModelRequests);
      console.log("All models results:", results);

      // TODO: Can't remember what we omit update/created dates and put them back in.
      // TODO: Was this because of different syntax before?
      const dataStore = callModels.map((modelName, index) => {
        if (!results[index] || !results[index].data) {
          throw `DATA: Was not able to retrieve all model data for ${modelName} - please try again in a few minutes.`;
        }
        const data = results[index].data;
        return {
          name: modelName,
          data,
        };
      });

      // 1. Get and organize models data
      const modelGroupData = validModels.reduce((arr: any[], model: any) => {
        const data = dataStore.find((ds) => ds.name === model.name)?.data || [];
        const dataWithRelations = getAllRelationData(
          data,
          model,
          dataStore,
          model.name,
        );
        // console.log("Publishing", model.name, dataWithRelations);
        return [
          ...arr,
          {
            name: model.name,
            data: dataWithRelations,
          },
        ];
      }, []);

      // 2. Get and organize single items data
      const singleItemsResults = await Promise.all(allSingleItemRequests);
      const modelAndSingleItemsTogether = singleItemsResults.reduce(
        (arr: any[], response) => {
          const objName = response.data?.name ? response.data.name : null;
          if (objName === null) return arr;
          const data = JSON.parse(response.data.data || `{}`);
          return [
            ...arr,
            {
              name: objName,
              data,
            },
          ];
        },
        modelGroupData,
      );

      // consolidate
      const consolidatedName = modelAndSingleItemsTogether.find(
        (i) => i.name === groupName,
      )
        ? `all_${groupName}`
        : groupName;
      const consolidated = {
        name: consolidatedName,
        data: modelAndSingleItemsTogether.reduce((obj, item) => {
          return {
            ...obj,
            [item.name]: item.data,
          };
        }, {}),
      };

      // make filepaths
      const filepaths_json = [
        consolidated,
        ...modelAndSingleItemsTogether,
      ].reduce((obj, item) => {
        return {
          ...obj,
          [makeFilePath(item.name)]: item.data,
        };
      }, {});

      // 4. publish all
      const publishObj = {
        filepaths_json,
      };
      console.log("Publishing:", publishObj);
      const response = await apiAgent.Models.publish(publishObj);

      if (response.status === 200) {
        tMsg.success(
          "Successfully published! Please note that content may take some time to go live.",
        );
        updatePublishTimes(groupName, setPublishTimes);
      } else throw response.statusText;
    } catch (err) {
      if (_.isString(err) && _.startsWith(err, "DATA")) {
        tMsg.error(
          "A data issue occurred during publishing - Refresh the page and try again in a few minutes.",
        );
      } else tMsg.error("Error when publishing. See Console for details.");
      console.log(err);
    } finally {
      setPublishing(false);
    }
  };
};

export { usePublisher };
